/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SystemGeneralTabGeneral.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the System General module.
----------------------------------------------------------------*/


import type { ReactNode } from 'react';
import { Globe } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import { useI18n } from '../../../hooks/useI18n';
import { LanguageSelector } from '../../extensions/components/LanguageSelector';
import { SystemGeneralSettings } from '../types/systemGeneral.types';
import { DEFAULT_VOICE } from '../../../data/formOptions';

interface Props {
    formData: SystemGeneralSettings;
    renderField: (field: keyof SystemGeneralSettings, control: ReactNode, options?: { onReset?: () => void }) => ReactNode;
    onChange: (field: keyof SystemGeneralSettings, value: SystemGeneralSettings[keyof SystemGeneralSettings]) => void;
    deriveDefaultVoice: (lang: string) => string;
}

export function SystemGeneralTabGeneral({ formData, renderField, onChange, deriveDefaultVoice }: Props) {
    const { t } = useI18n();

    return (
        <FormBox title={t('systemGeneral.tabs.general')} icon={<Globe className="h-5 w-5" />}>
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    {/* Language */}
                    {renderField(
                        'defaultLanguage',
                        <LanguageSelector
                            value={formData.defaultLanguage}
                            onChange={(value) => {
                                const lang = value.includes('-') && value.split('-').length > 2
                                    ? value.split('-').slice(0, -1).join('-')
                                    : value;
                                onChange('defaultLanguage', lang);
                                onChange('defaultVoice', deriveDefaultVoice(lang));
                            }}
                            selectedVoice={formData.defaultVoice || DEFAULT_VOICE}
                            onVoiceChange={(voice) => onChange('defaultVoice', voice)}
                            showVoiceSelector={false}
                            disabled={false}
                            showLabel={false}
                        />
                    )}

                    {/* Timezone */}
                    {renderField(
                        'defaultTimezone',
                        <TimezoneSelect
                            value={formData.defaultTimezone}
                            onChange={(value) => onChange('defaultTimezone', value)}
                        />
                    )}

                    <div className="col-span-full border-t border-border my-2" />

                    {/* Call Behavior */}
                    {renderField('ringTimeout', <Input type="number" min={10} max={120} value={formData.ringTimeout} onChange={(e) => onChange('ringTimeout', parseInt(e.target.value, 10))} />)}
                    {renderField('callWaiting', <Toggle value={formData.callWaiting} onChange={(v) => onChange('callWaiting', v)} />)}

                    {renderField('privacy',
                        <select value={formData.privacy} onChange={(e) => onChange('privacy', e.target.value)} className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground w-full">
                            <option value="none">{t('common.none')}</option>
                            <option value="screen">{t('systemGeneral.options.privacy.screen', 'Screen')}</option>
                            <option value="name">{t('systemGeneral.options.privacy.name', 'Name')}</option>
                            <option value="number">{t('systemGeneral.options.privacy.number', 'Number')}</option>
                            <option value="full">{t('systemGeneral.options.privacy.full', 'Full')}</option>
                        </select>
                    )}

                    {renderField('maxCalls', <Input type="number" min={0} max={100} value={formData.maxCalls} onChange={(e) => onChange('maxCalls', parseInt(e.target.value, 10))} />)}
                    {renderField('rejectAnonymous', <Toggle value={formData.rejectAnonymous} onChange={(v) => onChange('rejectAnonymous', v)} />)}
                    {renderField('callScreen', <Toggle value={formData.callScreen} onChange={(v) => onChange('callScreen', v)} />)}
                    {renderField('pinlessDialing', <Toggle value={formData.pinlessDialing} onChange={(v) => onChange('pinlessDialing', v)} />)}

                    {renderField('autoPasswordLength',
                        <div className="flex items-center gap-2">
                            <input type="range" min={10} max={50} step={5} value={formData.autoPasswordLength} onChange={(e) => onChange('autoPasswordLength', parseInt(e.target.value, 10))} className="flex-1" />
                            <span className="text-sm font-medium text-foreground w-12 text-right">{formData.autoPasswordLength}</span>
                        </div>
                    )}
                </div>
            </div>
        </FormBox>
    );
}
